import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { supabase } from "../lib/supabaseClient";
import AdminHeader from "../components/Admin/AdminHeader";

import "./AdminActivities.css";

const AdminActivities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    loadActivities();
  }, []);

  /* =========================================
     LOAD ACTIVITIES
     ========================================= */

  const loadActivities = async () => {
    setLoading(true);
    setError("");

    const { data, error } = await supabase
      .from("activities")
      .select(`
        id,
        title,
        slug,
        category,
        activity_date,
        location,
        description,
        cover_image,
        featured,
        published,
        created_at,
        updated_at
      `)
      .order("activity_date", {
        ascending: false,
        nullsFirst: false,
      });

    if (error) {
      console.error(
        "Error loading activities:",
        error
      );

      setError(error.message);
      setLoading(false);
      return;
    }

    setActivities(data || []);
    setLoading(false);
  };

  /* =========================================
     GET STORAGE PATH
     ========================================= */

  const getStoragePathFromUrl = (url) => {
    if (!url) {
      return null;
    }

    const marker =
      "/storage/v1/object/public/activities/";

    const index = url.indexOf(marker);

    if (index === -1) {
      return null;
    }

    return decodeURIComponent(
      url.substring(index + marker.length)
    );
  };

  /* =========================================
     DELETE STORAGE IMAGE
     ========================================= */

  const deleteStorageImage = async (url) => {
    const filePath =
      getStoragePathFromUrl(url);

    if (!filePath) {
      console.log(
        "No Supabase activity image to delete."
      );

      return true;
    }

    console.log(
      "Deleting activity image:",
      filePath
    );

    const { data, error } =
      await supabase.storage
        .from("activities")
        .remove([filePath]);

    if (error) {
      console.error(
        "Unable to delete activity image:",
        error
      );

      return false;
    }

    if (!data || data.length === 0) {
      console.warn(
        "Supabase returned 200 but no activity image was deleted:",
        filePath
      );

      return false;
    }

    console.log(
      "Activity image deleted successfully:",
      data
    );

    return true;
  };

  /* =========================================
     DELETE ACTIVITY
     ========================================= */

  const handleDelete = async (activity) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${activity.title}"? This will also delete all associated photos.`
    );

    if (!confirmed) {
      return;
    }

    setError("");
    setSuccess("");
    setActionLoading(true);

    try {
      /*
       * 1. Get all gallery images belonging
       *    to this activity.
       */
      const {
        data: galleryImages,
        error: galleryError,
      } = await supabase
        .from("activity_images")
        .select("id, image_url")
        .eq("activity_id", activity.id);

      if (galleryError) {
        throw galleryError;
      }

      /*
       * 2. Delete the activity database row.
       *
       * activity_images rows will be removed
       * automatically because activity_id uses
       * ON DELETE CASCADE.
       */
      const {
        data,
        error: deleteError,
      } = await supabase
        .from("activities")
        .delete()
        .eq("id", activity.id)
        .select();

      if (deleteError) {
        throw deleteError;
      }

      if (!data || data.length === 0) {
        throw new Error(
          "You do not have permission to delete this activity."
        );
      }

      /*
       * 3. Delete the cover image.
       */
      let imageCleanupWarning = false;

      if (activity.cover_image) {
        const deleted =
          await deleteStorageImage(
            activity.cover_image
          );

        if (!deleted) {
          imageCleanupWarning = true;
        }
      }

      /*
       * 4. Delete all gallery images.
       */
      if (
        galleryImages &&
        galleryImages.length > 0
      ) {
        for (const image of galleryImages) {
          if (!image.image_url) {
            continue;
          }

          const deleted =
            await deleteStorageImage(
              image.image_url
            );

          if (!deleted) {
            imageCleanupWarning = true;
          }
        }
      }

      /*
       * 5. Show result.
       */
      if (imageCleanupWarning) {
        setSuccess(
          "Activity deleted, but one or more photos could not be removed from Storage."
        );
      } else {
        setSuccess(
          "Activity and all associated photos deleted successfully."
        );
      }

      /*
       * 6. Refresh list.
       */
      await loadActivities();

    } catch (err) {
      console.error(
        "Error deleting activity:",
        err
      );

      setError(
        err?.message ||
          "Unable to delete the activity. Please try again."
      );
    } finally {
      setActionLoading(false);
    }
  };

  /* =========================================
     TOGGLE PUBLISHED
     ========================================= */

  const handleTogglePublished = async (
    activity
  ) => {
    setError("");
    setSuccess("");
    setActionLoading(true);

    const newPublishedState =
      !activity.published;

    const { data, error } =
      await supabase
        .from("activities")
        .update({
          published: newPublishedState,
          updated_at: new Date().toISOString(),
        })
        .eq("id", activity.id)
        .select();

    if (error) {
      console.error(
        "Error updating activity:",
        error
      );

      setError(error.message);
      setActionLoading(false);
      return;
    }

    if (!data || data.length === 0) {
      setError(
        "You do not have permission to update this activity."
      );

      setActionLoading(false);
      return;
    }

    setSuccess(
      newPublishedState
        ? "Activity published successfully."
        : "Activity moved to draft."
    );

    setActionLoading(false);

    await loadActivities();
  };

  /* =========================================
     FORMAT DATE
     ========================================= */

  const formatDate = (dateString) => {
    if (!dateString) {
      return "Date not set";
    }

    return new Date(
      `${dateString}T00:00:00`
    ).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  /* =========================================
     RENDER
     ========================================= */

  return (
    <div className="admin-page">

      <div className="admin-dashboard">

        {/* HEADER */}

        <AdminHeader
          backTo="/admin"
          backLabel="Dashboard"
        />

        {/* MAIN */}

        <main className="admin-main">

          {/* PAGE TITLE */}

          <section className="admin-activities-title">

            <div>
              <span className="admin-page-eyebrow">
                PRACTICE & COMMUNITY
              </span>

              <h1>Activities</h1>

              <div className="admin-title-divider"></div>

              <p>
                Document health initiatives,
                awareness programs and activities
                from the practice.
              </p>
            </div>

            <Link
              to="/admin/activities/new"
              className="admin-activities-add-btn"
            >
              + New Activity
            </Link>

          </section>

          {/* MESSAGES */}

          {error && (
            <div className="admin-error admin-message">
              {error}
            </div>
          )}

          {success && (
            <div className="admin-success admin-message">
              {success}
            </div>
          )}

          {/* ACTIVITY LIST */}

          <section className="admin-activities-card">

            <div className="admin-activities-card-header">

              <div>
                <span className="admin-section-label">
                  ACTIVITIES
                </span>

                <h2>
                  Practice Activities
                </h2>
              </div>

              <span className="admin-count">
                {activities.length}
                {activities.length === 1
                  ? " activity"
                  : " activities"}
              </span>

            </div>

            {loading ? (

              <div className="admin-loading">
                <div className="admin-spinner"></div>

                <p>
                  Loading activities...
                </p>
              </div>

            ) : activities.length === 0 ? (

              <div className="admin-activities-empty">

                <div className="admin-activities-empty-icon">
                  ✦
                </div>

                <h3>
                  No activities yet
                </h3>

                <p>
                  Add your first practice activity
                  to start documenting the work
                  happening beyond the consultation
                  room.
                </p>

                <Link
                  to="/admin/activities/new"
                  className="admin-primary-btn admin-empty-btn"
                >
                  Create First Activity
                </Link>

              </div>

            ) : (

              <div className="admin-activity-list">

                {activities.map(
                  (activity) => (

                    <article
                      key={activity.id}
                      className="admin-activity-row"
                    >

                      {/* MAIN INFORMATION */}

                      <div className="admin-activity-main">

                        {activity.cover_image ? (

                          <img
                            src={
                              activity.cover_image
                            }
                            alt=""
                            className="admin-activity-thumbnail"
                          />

                        ) : (

                          <div className="admin-activity-thumbnail admin-activity-thumbnail-placeholder">
                            ✦
                          </div>

                        )}

                        <div className="admin-activity-info">

                          <div className="admin-activity-meta">

                            <span className="admin-activity-category">
                              {activity.category ||
                                "ACTIVITY"}
                            </span>

                            <span
                              className={
                                activity.published
                                  ? "admin-status published"
                                  : "admin-status draft"
                              }
                            >
                              {activity.published
                                ? "Published"
                                : "Draft"}
                            </span>

                          </div>

                          <h3>
                            {activity.title}
                          </h3>

                          <div className="admin-activity-details">

                            {activity.location && (
                              <span>
                                {activity.location}
                              </span>
                            )}

                            {activity.activity_date && (
                              <span>
                                {formatDate(
                                  activity.activity_date
                                )}
                              </span>
                            )}

                          </div>

                          {activity.description && (
                            <p>
                              {activity.description}
                            </p>
                          )}

                        </div>

                      </div>

                      {/* ACTIONS */}

                      <div className="admin-activity-actions">

                        <Link
                          to={`/admin/activities/edit/${activity.id}`}
                          className="admin-edit-btn"
                        >
                          Edit
                        </Link>

                        <button
                          type="button"
                          onClick={() =>
                            handleTogglePublished(
                              activity
                            )
                          }
                          disabled={
                            actionLoading
                          }
                          className="admin-publish-btn"
                        >
                          {activity.published
                            ? "Unpublish"
                            : "Publish"}
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            handleDelete(
                              activity
                            )
                          }
                          disabled={
                            actionLoading
                          }
                          className="admin-delete-btn"
                        >
                          Delete
                        </button>

                      </div>

                    </article>

                  )
                )}

              </div>

            )}

          </section>

        </main>

      </div>

    </div>
  );
};

export default AdminActivities;